import clsx from "clsx";
import React, { forwardRef } from "react";
import { useIntl, FormattedMessage } from "react-intl";

import { capitalizeFirstLetter } from "../../utils/helpers";
import { svgIcon } from "../../utils/svgProvider";

interface CheckboxProps {
	id: string;
	className?: string;
	value?: boolean;
	error?: string;
	label?: string;
	linkText?: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onLinkClick?: () => void;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
	(props, ref) => {
		const {
			id,
			className,
			value,
			error,
			label,
			linkText,
			onChange,
			onLinkClick,
		} = props;
		const { formatMessage } = useIntl();

		const handleOnLinkClick = (e) => {
			e.preventDefault();
			if (e.target.getAttribute("data-trigger") === "anchor") {
				onLinkClick();
			}
		};

		const handleOnChange = ({ target: { id, checked } }) => {
			const event = {
				target: {
					id,
					value: checked,
					type: "checkbox",
				},
			} as React.ChangeEvent<HTMLInputElement>;
			onChange(event);
		};

		return (
			<div
				className={clsx({
					[`${className}`]: true,
					error: error,
				})}
			>
				<input
					ref={ref}
					className="checkbox"
					id={id}
					name={id}
					type="checkbox"
					checked={value}
					value={value ? "on" : "off"}
					aria-checked={value}
					onChange={handleOnChange}
					data-automationid={id}
					data-testid={`${id}.input`}
				/>
				<label
					role="checkbox"
					className="label"
					data-testid={`${id}.label`}
					htmlFor={id}
				>
					{value ? svgIcon.checkboxSelected : svgIcon.checkboxUnselected}
					<span>
						{label.trim()}
						{linkText && onLinkClick ? (
							<span>
								<a href="#" data-trigger="anchor" onClick={handleOnLinkClick}>
									{linkText}
								</a>
							</span>
						) : (
							<span dangerouslySetInnerHTML={{ __html: linkText }}></span>
						)}
					</span>
				</label>

				{error && (
					<span
						className="errorMessage"
						data-testid={`${id}.error`}
						data-automationid={`${id}.error`}
					>
						<FormattedMessage
							id={error}
							values={{
								fieldname: capitalizeFirstLetter(formatMessage({ id: label })),
							}}
						/>
					</span>
				)}
			</div>
		);
	},
);
